import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    IconButton,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { fetchDealers, updateDealer } from '../api/licenseService';
import EditDealerDialog from './EditDealerDialog';

const licenseTypes = ['Fixed', 'Subscription'];
const products = ['SuperPos', 'Lieferando'];

function EditLicenseDialog({ license, onClose, onSave }) {
    const [editedLicense, setEditedLicense] = useState(() => ({
        ...license,
        ownerDetails: license.ownerDetails || {
            company: '',
            vatId: '',
            name: '',
            firstName: '',
            email: '',
            phoneNumber: '',
            street: '',
            houseNameOrNumber: '',
            city: '',
            postCode: '',
            country: ''
        }
    }));
        
    const [dealers, setDealers] = useState([]);
    const [editDealer, setEditDealer] = useState(null);

    const isMainLicense = editedLicense.product === 'SuperPos';

    useEffect(() => {
        if (editedLicense.product === 'Lieferando' && !editedLicense.apiKeys) {
            setEditedLicense((prev) => ({
                ...prev,
                apiKeys: {
                    menuApiKey: '',
                    orderingApiKey: '',
                    restaurantId: ''
                }
            }));
        }
    }, [editedLicense.product]);

    useEffect(() => {
        const loadDealers = async () => {
            const apiKey = localStorage.getItem('apiKey');
            const res = await fetchDealers(apiKey);
            setDealers(res || []);
        };
        loadDealers();
    }, []);

    const handleChange = (field) => (e) => {
        setEditedLicense({ ...editedLicense, [field]: e.target.value });
    };

    const handleCheckboxChange = (field) => (e) => {
        setEditedLicense({ ...editedLicense, [field]: e.target.checked });
    };

    const handleOwnerDetailChange = (field) => (e) => {
        setEditedLicense({
            ...editedLicense,
            ownerDetails: {
                ...editedLicense.ownerDetails,
                [field]: e.target.value
            }
        });
    };

    const handleApiKeyChange = (field) => (e) => {
        setEditedLicense({
            ...editedLicense,
            apiKeys: {
                ...editedLicense.apiKeys,
                [field]: e.target.value
            }
        });
    };

    const handleSave = () => {
        onSave(editedLicense);
    };

    return (
        <>
            <Dialog open onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Lizenz bearbeiten</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Inhaber"
                        fullWidth
                        margin="normal"
                        value={editedLicense.owner || ''}
                        onChange={handleChange('owner')}
                    />
                    <TextField
                        select
                        label="Produkt"
                        fullWidth
                        margin="normal"
                        value={editedLicense.product || ''}
                        onChange={handleChange('product')}
                    >
                        {products.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>

                    {isMainLicense && (
                        <>
                            <TextField
                                label="Kassen Id"
                                fullWidth
                                margin="normal"
                                value={editedLicense.tillId || ''}
                                onChange={handleChange('tillId')}
                            />

                            <FormControl fullWidth margin="normal">
                                <InputLabel>Händler</InputLabel>
                                <Select
                                    value={editedLicense.distributor?.id || ''}
                                    onChange={(e) => {
                                        const selected = dealers.find(d => d.id === e.target.value);
                                        setEditedLicense({ ...editedLicense, distributor: selected });
                                    }}
                                >
                                    {dealers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Tooltip title="Händler bearbeiten">
                                <IconButton
                                    onClick={() => setEditDealer(editedLicense.distributor)}
                                    disabled={!editedLicense.distributor}
                                    size="small"
                                    sx={{ mt: 1 }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    <TextField
                        select
                        label="Lizenztyp"
                        fullWidth
                        margin="normal"
                        value={editedLicense.licenseType || ''}
                        onChange={handleChange('licenseType')}
                    >
                        {licenseTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedLicense.isActive || false}
                                onChange={handleCheckboxChange('isActive')}
                            />
                        }
                        label="Aktiv"
                        sx={{ mt: 1 }}
                    />

                    <TextField
                        label="Gültig bis"
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={
                            editedLicense.validUntil
                                ? new Date(editedLicense.validUntil).toISOString().split('T')[0]
                                : ''
                        }
                        onChange={handleChange('validUntil')}
                    />

                    {isMainLicense && editedLicense.ownerDetails && (
                        <>
                            <h4>Inhaberdaten</h4>
                            <TextField label="Firma" fullWidth margin="normal" value={editedLicense.ownerDetails.company || ''} onChange={handleOwnerDetailChange('company')} />
                            <TextField label="USt-ID" fullWidth margin="normal" value={editedLicense.ownerDetails.vatId || ''} onChange={handleOwnerDetailChange('vatId')} />
                            <TextField label="Nachname" fullWidth margin="normal" value={editedLicense.ownerDetails.name || ''} onChange={handleOwnerDetailChange('name')} />
                            <TextField label="Vorname" fullWidth margin="normal" value={editedLicense.ownerDetails.firstName || ''} onChange={handleOwnerDetailChange('firstName')} />
                            <TextField label="E-Mail" fullWidth margin="normal" value={editedLicense.ownerDetails.email || ''} onChange={handleOwnerDetailChange('email')} />
                            <TextField label="Telefonnummer" fullWidth margin="normal" value={editedLicense.ownerDetails.phoneNumber || ''} onChange={handleOwnerDetailChange('phoneNumber')} />
                            <TextField label="Straße" fullWidth margin="normal" value={editedLicense.ownerDetails.street || ''} onChange={handleOwnerDetailChange('street')} />
                            <TextField label="Hausnummer" fullWidth margin="normal" value={editedLicense.ownerDetails.houseNameOrNumber || ''} onChange={handleOwnerDetailChange('houseNameOrNumber')} />
                            <TextField label="Stadt" fullWidth margin="normal" value={editedLicense.ownerDetails.city || ''} onChange={handleOwnerDetailChange('city')} />
                            <TextField label="PLZ" fullWidth margin="normal" value={editedLicense.ownerDetails.postCode || ''} onChange={handleOwnerDetailChange('postCode')} />
                            <TextField label="Land" fullWidth margin="normal" value={editedLicense.ownerDetails.country || ''} onChange={handleOwnerDetailChange('country')} />
                        </>
                    )}

                    {editedLicense.product === 'Lieferando' && editedLicense.apiKeys && (
                        <>
                            <h4>API-Schlüssel</h4>
                            <TextField label="Menu API Key" fullWidth margin="normal" value={editedLicense.apiKeys.menuApiKey || ''} onChange={handleApiKeyChange('menuApiKey')} />
                            <TextField label="Ordering API Key" fullWidth margin="normal" value={editedLicense.apiKeys.orderingApiKey || ''} onChange={handleApiKeyChange('orderingApiKey')} />
                            <TextField label="Restaurant-ID" fullWidth margin="normal" value={editedLicense.apiKeys.restaurantId || ''} onChange={handleApiKeyChange('restaurantId')} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave}>Speichern</Button>
                </DialogActions>
            </Dialog>

            {editDealer && (
                <EditDealerDialog
                    dealer={editDealer}
                    onClose={() => setEditDealer(null)}
                    onSave={async (updated) => {
                        const apiKey = localStorage.getItem('apiKey');
                        await updateDealer(updated, apiKey);
                        setEditDealer(null);
                        setDealers((prev) =>
                            prev.map((d) => (d.id === updated.id ? updated : d))
                        );
                        if (editedLicense.distributor?.id === updated.id) {
                            setEditedLicense({ ...editedLicense, distributor: updated });
                        }
                    }}
                />
            )}
        </>
    );
}

export default EditLicenseDialog;
